import { useRef, useState, useEffect } from 'react';
import {
  Button, PinInput, Text,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useForm } from '@mantine/form';
import { useNavigate, generatePath } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { notifications } from '@mantine/notifications';
import { IconBrandTelegram } from '@tabler/icons-react';
import Page from '@/components/Page';
import validate from '@/helpers/validations';
import { getMutationResult } from '@/helpers/query';
import { buildUnifiedDisposalBoard } from '@/helpers/calculations';
import { useOtpMutation, useResendOtpMutation, useRetrievalMutation } from '@/services/api';
import routes from '@/routes';
import { login } from '@/data/features/auth/auth';
import { resetAuth } from '@/data/features/user/user';
import { updateOffer } from '@/data/features/offer/offer';
import classes from './otp.module.css';
import LoginImage from '@/assets/images/loginImage.svg?react'; // eslint-disable-line import/no-unresolved

const Otp = () => {
  const { t } = useTranslation();
  const pinRef = useRef(null);
  const [apiOtp] = useOtpMutation();
  const [apiResendOtp] = useResendOtpMutation();
  const [apiRetrieval] = useRetrievalMutation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false); // handle loading on continue button
  const [message, setMessage] = useState('');
  const dispatch = useDispatch();
  const userStore = useSelector((state) => state.user);
  const offerStore = useSelector((state) => state.offer);

  const { phone } = userStore;

  const required = t('globals.validations.required');
  // const otp = t('globals.validations.otp');

  const formValidations = {
    otp: (value) => validate(value, { required }),
  };

  const initialValues = {
    otp: '',
  };

  const [countdown, setCountdown] = useState(0);
  const [hasPermissionToResend, setHasPermissionToResend] = useState(true);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    const id = setInterval(() => setCountdown((c) => c - 1), 1_000);
    return () => clearInterval(id);
  }, [countdown]);

  /**
 * jiggle effect on pin input to show error to the user
 */
  const jiggle = () => {
    pinRef.current?.classList.add(classes.jiggle);

    setTimeout(() => {
      pinRef.current?.classList.remove(classes.jiggle);
    }, 200);
  };

  const form = useForm({
    mode: 'uncontrolled',
    initialValues,
    validate: formValidations,
  });

  const { onSubmit } = form;

  const onError = (errors) => {
    jiggle();
    setMessage(errors.otp);
  };

  const handleSubmit = async (values) => {
    try {
      setMessage('');
      setLoading(true);
      const { data, isError } = await getMutationResult(values, apiOtp);

      if (isError) {
        jiggle();
        setLoading(false);

        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
      } else {
        const {
          accessToken,
          refreshToken,
        } = data;

        dispatch(login({ token: accessToken, refreshToken }));

        const offerId = offerStore?.id;
        let calculatedOffer = null;

        if (offerId) {
          const { data: offerRes, isError: isOfferError } = await
          getMutationResult({ id: offerId }, apiRetrieval);
          if (!isOfferError && offerRes?.data) {
            calculatedOffer = offerRes.data;
            const { isRecycling, regularOffer } = calculatedOffer;

            if (isRecycling) {
              const regularDisposalBoard = regularOffer.mixes;
              const cycleOffer = calculatedOffer.cycleOffer.repaymentTable;
              const updatedDisposalBoard = buildUnifiedDisposalBoard(
                regularDisposalBoard,
                cycleOffer,
              );
              calculatedOffer.mixes = updatedDisposalBoard;
            }
            dispatch(updateOffer(calculatedOffer));
          }
        }

        if (calculatedOffer.loan.title === 'mortgageRefinancing') {
          navigate(routes.mortgageCycleSummary.path);
        } else {
          navigate(routes.summary.path);
        }
      }
    } catch (error) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (countdown > 0 || !hasPermissionToResend) return;

    try {
      const result = await getMutationResult({}, apiResendOtp);

      if (result.isError) {
        if (result.status === 401) {
          dispatch(resetAuth());
          setHasPermissionToResend(false);
        } else {
          notifications.show({
            title: t('globals.error.title'),
            message: t('globals.error.message'),
          });
        }
        return;
      }

      setCountdown(30);
    } catch (err) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    }
  };

  const actions = <Button loading={loading} onClick={onSubmit((values) => handleSubmit(values), onError)}>{t('pages.login.action')}</Button>;
  const side = (
    <div className={classes.sideImage}>
      <LoginImage />
    </div>
  );

  return (
    <Page
      side={side}
      actions={actions}
      title={t('pages.otp.title')}
      subTitle={
        <>
          {t('pages.otp.subTitle')}
          <span className={classes.phone}> {phone}</span>
        </>}
    >
      <form>
        <div className={classes.otp}>
          <PinInput
            className={classes.otp}
            key={form.key('otp')}
            rootRef={pinRef}
            {...form.getInputProps('otp')}
          />
          {message && <Text c="red">{message}</Text>}
        </div>
        <Text>{t('pages.otp.resendText')}
          <Button
            variant="transparent"
            onClick={resendOtp}
            leftSection={<IconBrandTelegram size={19} />}
            disabled={countdown > 0 || !hasPermissionToResend}
          >
            {t('pages.otp.resendButton')}
            {countdown > 0 && ` (${countdown})`}
          </Button>
          {!hasPermissionToResend
            && <Text>{t('pages.otp.tooManyAttempts')}
              <Button
                variant="transparent"
                onClick={() => {
                  const toLogin = generatePath(
                    routes.login.path,
                    { proposalNumber: offerStore.id },
                  );
                  navigate(toLogin);
                }}>
                {t('pages.otp.backToLogin')}
              </Button>
            </Text>}
        </Text>
      </form>
    </Page>
  );
};

export default Otp;
